import { type ElementRef, forwardRef, memo, useCallback } from 'react';
import isEqual from 'lodash/isEqual';
import { RestaurantToShow } from '@discover-data/stores/restaurant/model/types';
import { useStoreState } from '@discover-data/stores/restaurant/hooks';
import shouldShowDiscountSection from '@discover-data/stores/restaurant/utils/discountAvailability/shouldShowDiscountSection';
import shouldShowRegularSection from '@discover-data/stores/restaurant/utils/regularAvailability/shouldShowRegularSection';
import RestaurantCardRaw from '@discover-components/restaurant/card';
import getRestaurantChips from '@discover-components/restaurant/card/getRestaurantChips';
import RegularAvailabilitySection from '@discover-components/availability/regular/section';
import DiscountAvailabilitySection from '@discover-components/availability/discount/section';
import useIsRowLayout from '@discover-components/availability/hooks/useIsRowLayout';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import { DiscountProvider } from '@/components/context/DiscountContext';

const RestaurantCard = memo(RestaurantCardRaw);

export type RestaurantListItemProps = {
  restaurant: RestaurantToShow;
  index: number;
  imagePriority: boolean;
  processingBooking: boolean;
  setProcessingBooking: (processingBooking: boolean) => void;
  onOpenReviewSummary?: (restaurantId: number) => void;
};

const RestaurantListItem = forwardRef<ElementRef<typeof RestaurantCard>, RestaurantListItemProps>(
  (
    {
      restaurant,
      index,
      imagePriority,
      processingBooking,
      setProcessingBooking,
      onOpenReviewSummary,
    },
    ref
  ) => {
    const { id, menuTitle } = restaurant;
    const handleOpenReviewSummary = useCallback(
      () => onOpenReviewSummary?.(id),
      [id, onOpenReviewSummary]
    );

    const session = useStoreState((state) => state.session.value);
    const people = useStoreState((state) => state.people.value);
    const isRowLayout = useIsRowLayout();

    let availabilitySections: JSX.Element[] = [];

    const showDiscountSection = useStoreState((state) =>
      shouldShowDiscountSection(
        state.regularOnly.value,
        state.byId.value,
        state.hasLastTable.value,
        state.discountAvailabilityById,
        state.effectiveDate,
        state.firebaseAvailabilitySessions,
        state.times.isSet,
        state.times.start,
        state.times.end,
        state.discountDatesToShowById,
        state.discountOnly.value,
        restaurant.id,
        state.mode === 'single',
        state.firebaseAllAvailability.loading,
        state.skipInitialFirebaseFetch,
        state.people.value
      )
    );

    if (showDiscountSection) {
      availabilitySections = [
        <DiscountAvailabilitySection
          key="discount-availability"
          restaurant={restaurant}
          processingBooking={processingBooking}
          setProcessingBooking={setProcessingBooking}
          showChips={false}
          soldOutTimesExperimentEligible
        />,
      ];
    }

    const showRegularSection = useStoreState((state) =>
      shouldShowRegularSection(
        state.discountOnly.value,
        state.hasLastTable.value,
        state.byId.value,
        state.session.value,
        state.people.value,
        state.times.isSet,
        state.regularAvailabilityById,
        state.effectiveDate,
        state.mode === 'single',
        restaurant.id,
        showDiscountSection
      )
    );

    if (showRegularSection) {
      availabilitySections.push(
        <RegularAvailabilitySection key="regular-availability" restaurant={restaurant} />
      );
    }

    return (
      <TrackProvider
        key={`RestaurantListItem${id}`}
        trackEvent="restaurant_impression"
        component_name="restaurant card"
        index={index}
        component_detail={menuTitle}
        component_id={id}
        eventParams={{
          source_page_id: id,
          action: 'viewed',
          impression: 'restaurant',
        }}>
        <DiscountProvider defaultDiscount={0.5}>
          <RestaurantCard
            restaurant={restaurant}
            restaurantChips={
              showDiscountSection
                ? getRestaurantChips(restaurant, session, people, isRowLayout)
                : undefined
            }
            imagePriority={imagePriority}
            availabilitySections={availabilitySections}
            session={session}
            people={people}
            onOpenReviewSummary={onOpenReviewSummary ? handleOpenReviewSummary : undefined}
            ref={ref}
          />
        </DiscountProvider>
      </TrackProvider>
    );
  }
);

RestaurantListItem.displayName = 'RestaurantListItem';

export default memo(RestaurantListItem, isEqual);
